import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { money, shortDate, type Bonspiel } from '@lcc/shared';
import { api } from '../lib/api';
import { Card, Empty, ErrorNote, Loading, Pill, Status } from '../components/ui';
import { PageHeader } from '../App';

export function BonspielsPage() {
  const spiels = useQuery({
    queryKey: ['bonspiels'],
    queryFn: () => api.get<{ data: Bonspiel[] }>('/bonspiels').then((r) => r.data),
  });

  const open = spiels.data?.filter((s) => s.kind === 'open') ?? [];
  const inHouse = spiels.data?.filter((s) => s.kind === 'in_house') ?? [];

  return (
    <>
      <PageHeader title="Bonspiels"
        sub={spiels.data ? `${spiels.data.length} this season` : undefined} />
      <div className="content stack">
        {spiels.isLoading ? <Loading what="Loading bonspiels" />
          : spiels.error ? <ErrorNote error={spiels.error} />
          : !spiels.data?.length ? <Card><Empty title="No bonspiels this season" /></Card>
          : (
            <>
              <SpielTable title="Open spiels" spiels={open} />
              <SpielTable title="In-house spiels" spiels={inHouse} />
            </>
          )}
      </div>
    </>
  );
}

function SpielTable({ title, spiels }: { title: string; spiels: Bonspiel[] }) {
  if (spiels.length === 0) return null;
  return (
    <Card title={`${title} (${spiels.length})`} flush>
      <div className="table-wrap">
        <table className="t">
          <thead>
            <tr>
              <th>Spiel</th><th>Dates</th><th className="num">Entries</th>
              <th className="num">Fee</th><th>Entries open</th><th>Sponsor</th>
            </tr>
          </thead>
          <tbody>
            {spiels.map((s) => {
              const full = s.maxTeams !== null && (s.entryCount ?? 0) >= s.maxTeams;
              return (
                <tr key={s.id}>
                  <td><Link to={`/bonspiels/${s.id}`}>{s.name}</Link></td>
                  <td className="small nowrap muted">
                    {s.startsOn === s.endsOn
                      ? shortDate(s.startsOn)
                      : `${shortDate(s.startsOn)} – ${shortDate(s.endsOn)}`}
                  </td>
                  <td className="num">
                    {s.entryCount ?? 0}{s.maxTeams ? ` / ${s.maxTeams}` : ''}
                    {full && <> <Pill tone="warn">full</Pill></>}
                  </td>
                  <td className="num">{s.entryFeeCents ? money(s.entryFeeCents) : '—'}</td>
                  <td><Status value={s.registrationOpen ? 'open' : 'cancelled'} /></td>
                  <td className="small muted">{s.sponsorText ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
